import React, {useState, useEffect} from 'react';
import {useForm} from 'react-hook-form';
import {Controller} from 'react-hook-form';

import {fetchSelectData} from './../../../utils';
import SelectInput from '../../../components/ui/Inputs/SelectInput/SelectInput';
import TextInput from '../../../components/ui/Inputs/TextInput';
import TextAreaInput from '../../../components/ui/Inputs/TextAreaInput';
import Button from '../../../components/ui/Button/Button';
import FileDragNDropInput from './../../../components/ui/Inputs/FileDragNDropInput';
import {notify} from '../../../components/ui/Toast/Toast';
import {ContractorActions, FileActions, ContractorInvoiceActions} from '../../../actions';
import Error from '../../../components/ui/Error/Error';
import {getErrors} from './../../../utils';

function AssignNewInvoiceContractor({userData, invoiceData, invoiceContractorToBeUpdated, setView, refreshData}) {
    // -- states --
    const [disabled, setDisabled] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState(null);
    const [contractors, setContractors] = useState(null);

    // -- react hook form --
    const defaultValues = {
        contractor: null,
        amount: null,
        notes: null,
        gross_amount: null,
        recoverable_vat: null,
        file: null
    };
    const {handleSubmit, control, reset, formState: {isValid}} = useForm({defaultValues});

    // -- fill invoiceContractor values in form for update --
    useEffect(() => {
        if (invoiceContractorToBeUpdated && invoiceContractorToBeUpdated.id) {
            reset({
                contractor: invoiceContractorToBeUpdated.contractor,
                amount: (+invoiceContractorToBeUpdated.amount).toString(),
                gross_amount: (+invoiceContractorToBeUpdated.gross_amount).toString(),
                recoverable_vat: (+invoiceContractorToBeUpdated.recoverable_vat).toString(),
                notes: invoiceContractorToBeUpdated.notes,
                file: invoiceContractorToBeUpdated.file
            });
        }
    }, [invoiceContractorToBeUpdated]);

    // -- functions that get data for Select Inputs --
    const handleGetContractors = async (search, page = 1) => {
        await fetchSelectData({action: ContractorActions.getAllContractors, search:`&search=${search}&deal=${invoiceData?.deal?.id}`, page, setter: setContractors, userData, resultPath: 'data'});
    };

    useEffect(() => {
        // ↓ contractors dropdown values
        (async () => {
            await handleGetContractors('', 1);
        })();

    }, []);

    // -- handle submit --
    const onSubmit = async (data) => {
        setDisabled(true);
        setLoading(true);
        setErrors(null);

        let file = null;

        if (data.file && data.file.uid && data.file.type) {
            const newFile = {
                "type": data.file.type,
                "name": data.file.name,
                "file": data.file
            }

            const result = await FileActions.createFile(userData, newFile);

            if (!result.success) {
                setErrors(getErrors(result));
            }
            else {
                file = result.data;
            }
        }

        // ↓ body to send to the post/patch request (see API documentation)
        const contractor = {
            contractor_id: data.contractor?.id,
            invoice_id: invoiceData.id,
            amount: data.amount,
            gross_amount: data.gross_amount,
            recoverable_vat: data.recoverable_vat,
            notes: data.notes
        };

        if (file) {
            contractor.file_id = file.id;
        }

        // ↓ post - new invoiceContractor
        if (!invoiceContractorToBeUpdated) {
            const result = await ContractorInvoiceActions.createContractorInvoice(userData, contractor);

            if (!result.success) {
                setErrors(getErrors(result));
            }
            else {
                await refreshData();
                notify('Fournisseur assigné');

                setView(false);
                reset();
            }

            setLoading(false);
            setDisabled(false);
        }
        // ↓ patch - update invoiceContractor
        else {
            const result = await ContractorInvoiceActions.updateContractorInvoice(userData, contractor, invoiceContractorToBeUpdated.id);

            if (!result.success) {
                setErrors(getErrors(result));
            }
            else {
                await refreshData();
                notify('La factures sous/co traitants à bien été modifié');

                setView(false);
                reset();
            }

            setLoading(false);
            setDisabled(false);
        }
    };

    return (
        <div>
            <div className="pb-4 shadow-custom-outer">
                <div className="py-4 pl-4">
                    <h3 className="text-xl font-bold title">
                        {(invoiceContractorToBeUpdated && invoiceContractorToBeUpdated.id) ? 'Éditer la facture sous/co-traitant' : 'Nouvelle facture sous/co-traitant'}
                    </h3>
                </div>
                <div className="flex flex-row justify-between py-6 pl-4 pr-4 shadow-custom-inner">
                    <div>
                        <div className="flex mb-2">
                            <Controller
                                name="contractor"
                                control={control}
                                render={({
                                    field: {onChange, value, ref},
                                    fieldState: {error}
                                }) => (
                                    <SelectInput
                                        label='Fournisseur'
                                        placeholder='Fournisseur'
                                        inputRef={ref}
                                        value={value}
                                        error={error}
                                        options={contractors?.options}
                                        hasNextPage={contractors?.hasNextPage}
                                        loading={!contractors}
                                        onChange={onChange}
                                        isSearchable={true}
                                        fetchFunction={handleGetContractors}
                                        labelKeys={['name']}
                                        required={true}
                                        disabled={(invoiceContractorToBeUpdated && invoiceContractorToBeUpdated.id) ? true : disabled}
                                    />
                                )}
                            />
                        </div>
                       <div className="flex mb-2">
                            <Controller
                                name="amount"
                                control={control}
                                rules={{required: {
                                    value: true,
                                    message: 'Veuillez saisir la TVA.'
                                }}}
                                render={({
                                    field: {onChange, value},
                                    fieldState: {error}
                                }) => (
                                    <TextInput
                                        label={'Montant HT (€)'}
                                        onChange={onChange}
                                        value={value}
                                        error={error}
                                        required={true}
                                        disabled={disabled}
                                        placeholder="Montant HT"
                                        type="number"
                                    />
                                )}
                            />
                        </div>
                    </div>

                    <div className="flex flex-col place-content-end">
                        <div className="flex mb-2">
                            <Controller
                                name="recoverable_vat"
                                control={control}
                                rules={{required: {
                                    value: true,
                                    message: 'Veuillez saisir la TVA.'
                                }}}
                                render={({
                                    field: {onChange, value},
                                    fieldState: {error}
                                }) => (
                                    <TextInput
                                        label={'TVA recupérable'}
                                        onChange={onChange}
                                        value={value}
                                        error={error}
                                        required={true}
                                        disabled={disabled}
                                        placeholder="TVA recupérable"
                                        type="number"
                                    />
                                )}
                            />
                        </div>

                        <div className="flex mb-2">
                            <Controller
                                name="gross_amount"
                                control={control}
                                rules={{required: {
                                    value: true,
                                    message: 'Veuillez saisir la TVA.'
                                }}}
                                render={({
                                    field: {onChange, value},
                                    fieldState: {error}
                                }) => (
                                    <TextInput
                                        label={'Montant TTC (€)'}
                                        onChange={onChange}
                                        value={value}
                                        error={error}
                                        required={true}
                                        disabled={disabled}
                                        placeholder="Montant TTC"
                                        type="number"
                                    />
                                )}
                            />
                        </div>
                    </div>
                </div>

                <div className="px-4 mb-5">
                    <Controller
                        name="file"
                        control={control}
                        // rules={{required: {
                        //     value: true,
                        //     message: 'Veuillez charger un logo'
                        // }}}
                        render={({
                            field: {onChange}
                        }) => (
                            <FileDragNDropInput
                                onChange={onChange}
                                uploadText='Glissez déposez ou séllectionnez un fichiers'
                                uploadHint="Ajoutez ici des documents (PDF, JPEG, PDF)"
                                maxCount={1}
                            />
                        )}
                    />
                </div>

                <div className="px-4">
                    <div className="py-4 border-t border-borderGrey">
                        <Controller
                            name="notes"
                            control={control}
                            render={({
                                field: {onChange, value}
                            }) => (
                                <TextAreaInput
                                    placeholder={'Tapez votre texte'}
                                    maxLength={100}
                                    onChange={onChange}
                                    value={value}
                                    disabled={disabled}
                                />
                            )}
                        />
                    </div>
                </div>

                <div className="flex flex-col justify-center mt-6 mb-4">
                    <div className="flex justify-center">
                        <Button
                            onClick={() => {
                                reset();
                                setView(false);
                            }}
                            type={'secondary'}
                            content={'Annuler'}
                            disabled={disabled}
                        />
                        {
                            (!invoiceContractorToBeUpdated) ?
                                <Button
                                    type={'primary'}
                                    content={'Ajouter la facture sous/co-traitant'}
                                    onClick={handleSubmit(onSubmit)}
                                    loading={loading}
                                    disabled={!isValid}
                                /> : null
                        }
                        {
                            (invoiceContractorToBeUpdated && invoiceContractorToBeUpdated.id) ?
                                <Button
                                    type={'primary'}
                                    content={'Modifier la facture sous/co-traitant'}
                                    onClick={handleSubmit(onSubmit)}
                                    loading={loading}
                                    disabled={(!contractors) ? true : !isValid}
                                /> : null
                        }
                    </div>
                    <Error errors={errors}/>
                </div>
            </div>
        </div>
    );
}

export default AssignNewInvoiceContractor;
